import { Button, Textbox, useScanner, useValidationResultDialog } from '@nest/uikit-react';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTerminalClient } from '../client-rpc-router';
import { HandlingUnitSelectDialog } from './HandlingUnitSelectDialog';

interface HandlingUnitScanInputProps {
	value?: any;
	onChange?: (value: any) => void;
	placeholder?: string;
	autoFocus?: boolean;
	disabled?: boolean;
}

export function HandlingUnitScanInput({ value, onChange, placeholder, autoFocus, disabled }: HandlingUnitScanInputProps) {
	const client = useTerminalClient();
	const scanner = useScanner();
	const [code, setCode] = useState<string>('');
	const [isEditing, setIsEditing] = useState<boolean>(!value);

	const scanHandlingUnitMutation = useMutation({
		mutationFn: (code: string) => client.wms.findHandlingUnitByCodeQuery.execute({ code }),
		onSuccess: result => {
			if (result) {
				onChange?.(result);
				setCode('');
				setIsEditing(false);
			}
		}
	});

	useValidationResultDialog([scanHandlingUnitMutation.error]);

	useEffect(() => {
		if (isEditing && scanner.code && !disabled) {
			handleScan(scanner.code);
		}
	}, [scanner.code, isEditing, disabled]);

	useEffect(() => {
		setIsEditing(!value);
	}, [value]);

	const handleScan = (scannedCode: string) => {
		setCode(scannedCode);
		scanHandlingUnitMutation.mutate(scannedCode);
	};

	const [isDialogOpen, setIsDialogOpen] = useState(false);

	const displayValue = typeof value === 'string' ? value : (value?.code ?? value?.name ?? (value?.id != null ? String(value.id) : ''));

	return (
		<div className="flex gap-2 items-center w-full">
			{!isEditing && value ? (
				<div className="flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2">
					<div className="text-nowrap text-ellipsis overflow-hidden">{displayValue}</div>
					<Button type="button" variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
						Změnit
					</Button>
				</div>
			) : (
				<>
					<Textbox
						value={code}
						onChange={e => setCode(e.target.value)}
						placeholder={placeholder || 'Naskenujte klec...'}
						autoFocus={autoFocus}
						disabled={disabled || scanHandlingUnitMutation.isPending}
						onKeyDown={e => {
							if (e.key === 'Enter') {
								e.preventDefault();
								handleScan(code);
							}
						}}
					/>
					<Button type="button" variant="outline" onClick={() => setIsDialogOpen(true)} disabled={disabled}>
						Vybrat
					</Button>
				</>
			)}

			<HandlingUnitSelectDialog
				open={isDialogOpen}
				onOpenChange={setIsDialogOpen}
				onSelect={hu => {
					onChange?.(hu);
					setIsDialogOpen(false);
					setIsEditing(false);
				}}
			/>
		</div>
	);
}
